import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MilesToKilometersConverter = () => {
  const [inputData, setInputData] = useState({
    miles: '',
    kilometers: '',
    result: ''
  });

  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value,
      result: ''
    }));
    setResults(null);
  };

  const convert = () => {
    const miles = parseFloat(inputData.miles);
    if (!isNaN(miles)) {
      const kilometers = miles * 1.60934;
      const resultText = `${miles} miles = ${kilometers.toFixed(4)} kilometers`;
      
      // Create chart data
      const chartData = {
        labels: ['Miles', 'Kilometers'],
        datasets: [
          {
            label: 'Distance',
            data: [miles, kilometers],
            backgroundColor: ['rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
            borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
            borderWidth: 1
          }
        ]
      };

      setInputData(prev => ({
        ...prev,
        kilometers: kilometers.toFixed(4),
        result: resultText
      }));

      setResults({
        miles,
        kilometers: kilometers.toFixed(4),
        resultText,
        chartData
      });
    }
  };

  const exampleData = {
    miles: '10',
    kilometers: '16.0934',
    result: '10 miles = 16.0934 kilometers'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-6"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <Helmet>
          <title>Miles to Kilometers Converter | Distance Conversion Tool</title>
          <meta name="description" content="Convert miles to kilometers with this simple calculator. This tool helps you quickly convert distances from miles to kilometers using the standard conversion factor." />
        </Helmet>

        <h1 className="text-3xl font-bold text-gray-800 mb-6">Miles to Kilometers Converter</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Miles (mi)
            </label>
            <input
              type="number"
              name="miles"
              value={inputData.miles}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter miles"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Kilometers (km)
            </label>
            <input
              type="text"
              name="kilometers"
              value={inputData.kilometers}
              readOnly
              className="w-full px-4 py-2 border rounded-lg bg-gray-50"
            />
          </div>
        </div>

        <button
          onClick={convert}
          className="bg-blue-500 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-600 transition-colors"
        >
          Convert
        </button>

        {inputData.result && (
          <div className="mt-6 p-4 bg-green-50 text-green-700 rounded-lg">
            {inputData.result}
          </div>
        )}

        {results && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Conversion Graph</h2>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Bar
                data={results.chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Distance'
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Unit'
                      }
                    }
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: 'Miles vs Kilometers Comparison'
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Example Calculation Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">Sample Conversion</h3>
            <p className="mb-2">Input: {exampleData.miles} miles</p>
            <p className="mb-2">Conversion: {exampleData.miles} × 1.60934 = {exampleData.kilometers} kilometers</p>
            <p className="mb-2">Result: {exampleData.result}</p>
            <p className="mt-4 text-gray-600">
              This example shows how 10 miles converts to approximately 16.0934 kilometers using the standard conversion factor.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">What is the conversion factor between miles and kilometers?</h3>
              <p>The conversion factor is 1 mile = 1.60934 kilometers.</p>
            </div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Why convert miles to kilometers?</h3>
              <p>Kilometers are the standard unit of measurement in most countries for road distances, while miles are primarily used in the United States, United Kingdom, and a few other countries.</p>
            </div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Is this converter accurate?</h3>
              <p>This converter uses the internationally accepted conversion factor and provides results accurate to four decimal places.</p>
            </div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Can I use decimal values?</h3>
              <p>Yes, you can enter decimal values (e.g., 5.5) to get precise conversions.</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MilesToKilometersConverter;